import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Radar as RadarIcon, Sparkles, Users, Target, Handshake,
  ArrowRight, ShieldCheck, MapPin, Zap, Check,
} from "lucide-react";
import RadarMap from "@/components/RadarMap";

const NAV_ITEMS = [
  { label: "Signals", href: "#features" },
  { label: "Hangouts", href: "#hangouts" },
  { label: "Advisor", href: "#advisor" },
  { label: "Pricing", href: "#pricing" },
];

const FEATURES = [
  {
    id: "radar", title: "Live Proximity Radar",
    body: "Real GPS. Real map. A live radar overlay shows humans within 25 km. Zero manual refresh — a subtle ping the moment someone enters your range.",
    accent: "cyan", icon: RadarIcon, testid: "feature-radar",
  },
  {
    id: "ai", title: "AI Chemistry Score",
    body: "Claude Sonnet 4.6 reads both profiles and returns a 0-100 compatibility score with a one-line reason and a ready-to-send opener. Cached per pair — instant on repeat visits.",
    accent: "magenta", icon: Sparkles, testid: "feature-ai",
  },
  {
    id: "handshake", title: "Signal Handshakes",
    body: "No unsolicited DMs. Send a Signal → they accept or decline. Decline auto-blocks them from ever pinging you again. Consent baked in at the protocol level.",
    accent: "cyan", icon: Handshake, testid: "feature-handshake",
  },
  {
    id: "hangout", title: "Group Hangouts",
    body: "Any group member can start a live Hangout. For the duration, everyone in the hangout sees each other's real position on the map. Ends automatically when the last person leaves.",
    accent: "green", icon: Users, testid: "feature-hangout",
  },
  {
    id: "advisor", title: "Goals + AI Advisor",
    body: "Set goals, get concise AI coaching. Every action the AI proposes needs your private cryptographic signature — the model never executes, only suggests.",
    accent: "magenta", icon: Target, testid: "feature-advisor",
  },
];

const AVATARS = [
  "https://images.unsplash.com/photo-1533461502717-83546f485d24?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwzfHxwb3J0cmFpdCUyMG5lb24lMjBsaWdodGluZ3xlbnwwfHx8fDE3ODU4OTAwODl8MA&ixlib=rb-4.1.0&q=85",
  "https://images.pexels.com/photos/7062120/pexels-photo-7062120.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.pexels.com/photos/7315526/pexels-photo-7315526.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
];

const accentClasses = (a) => ({
  cyan: { text: "text-[#00E5FF]", border: "border-[#00E5FF]/25", bg: "bg-[#00E5FF]/5", glow: "shadow-[0_0_60px_rgba(0,229,255,0.15)]" },
  magenta: { text: "text-[#FF007F]", border: "border-[#FF007F]/25", bg: "bg-[#FF007F]/5", glow: "shadow-[0_0_60px_rgba(255,0,127,0.15)]" },
  green: { text: "text-[#39FF14]", border: "border-[#39FF14]/25", bg: "bg-[#39FF14]/5", glow: "shadow-[0_0_60px_rgba(57,255,20,0.15)]" },
}[a]);

function Nav() {
  return (
    <nav className="fixed top-0 inset-x-0 z-50 glass border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between">
        <Link to="/" data-testid="nav-brand" className="flex items-center gap-2">
          <span className="relative inline-flex w-2.5 h-2.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-[#00E5FF] opacity-75 animate-ping" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#00E5FF]" />
          </span>
          <span className="font-[Unbounded] font-bold tracking-tighter text-lg">SIGNAL·RADAR</span>
          <span className="text-[9px] font-mono tracking-widest text-white/40 ml-1 hidden sm:inline">jair.one</span>
        </Link>
        <div className="hidden md:flex items-center gap-8">
          {NAV_ITEMS.map((n) => (
            <a key={n.href} href={n.href} className="text-xs font-mono tracking-widest text-white/60 hover:text-[#00E5FF] transition-colors">{n.label.toUpperCase()}</a>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Link to="/auth" data-testid="nav-signin" className="text-xs font-mono tracking-widest text-white/70 hover:text-white px-3 py-2 transition-colors">SIGN IN</Link>
          <Link to="/auth" data-testid="nav-signup" className="inline-flex items-center gap-1 bg-[#00E5FF] text-black text-xs font-semibold px-4 py-2 rounded-full hover:glow-primary transition-shadow">
            Start Scanning <ArrowRight size={12} />
          </Link>
        </div>
      </div>
    </nav>
  );
}

function Hero() {
  const [statsTick, setStatsTick] = useState(0);
  useEffect(() => { const t = setInterval(() => setStatsTick((n) => n + 1), 2500); return () => clearInterval(t); }, []);
  const orbitDots = [
    { top: "18%", left: "22%", d: 0 },
    { top: "68%", left: "78%", d: 0.4 },
    { top: "42%", left: "88%", d: 0.9 },
    { top: "78%", left: "18%", d: 1.4 },
    { top: "26%", left: "72%", d: 1.9 },
  ];
  return (
    <section className="relative min-h-screen overflow-hidden grain">
      {/* Map beneath */}
      <div className="absolute inset-0 opacity-40">
        <RadarMap lat={40.7128} lng={-74.006} />
      </div>
      {/* Radial fade */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(ellipse at 50% 60%, transparent 0%, rgba(5,5,10,0.7) 55%, #05050A 90%)",
      }} />
      {/* Grid overlay */}
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-32 md:pt-40 pb-24 grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-12 items-center">
        <div className="fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#00E5FF]/25 bg-[#00E5FF]/5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00E5FF] glow-primary" />
            <span className="text-[10px] font-mono tracking-[0.3em] text-[#00E5FF]">PROXIMITY · SOCIAL · DISCOVERY</span>
          </div>
          <h1 className="mt-6 font-[Unbounded] font-black tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
            Find your people,<br />
            <span className="text-[#00E5FF]">right here</span>,<br />
            right now.
          </h1>
          <p className="mt-6 text-base md:text-lg text-white/60 max-w-xl leading-relaxed">
            Live GPS radar. AI chemistry scoring. Consent-first handshake DMs.
            Meaningful connections in real local time — no swipes, no noise.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/auth" data-testid="hero-signup-btn"
              className="group inline-flex items-center gap-2 bg-[#00E5FF] text-black font-semibold px-6 py-3.5 rounded-full hover:glow-primary transition-shadow">
              Start Scanning
              <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <Link to="/auth" data-testid="hero-signin-btn"
              className="inline-flex items-center gap-1 border border-white/15 text-white/80 font-semibold px-6 py-3.5 rounded-full hover:border-white/50 transition-colors">
              I already have an account
            </Link>
          </div>
          <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
            {[
              { k: "SCAN RADIUS", v: "25 KM" },
              { k: "AI MODEL", v: "SONNET 4.6" },
              { k: "REACTIVITY", v: "< 14 MS" },
            ].map((s) => (
              <div key={s.k}>
                <div className="text-[9px] font-mono tracking-[0.25em] text-white/40">{s.k}</div>
                <div className="mt-1 font-[Unbounded] font-bold text-lg tracking-tighter">{s.v}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Radar visualization */}
        <div className="relative aspect-square max-w-md mx-auto w-full">
          <div className="absolute inset-0 rounded-full border border-[#00E5FF]/20" />
          <div className="absolute inset-[14%] rounded-full border border-[#00E5FF]/25" />
          <div className="absolute inset-[28%] rounded-full border border-[#00E5FF]/30" />
          <div className="absolute inset-[42%] rounded-full border border-[#00E5FF]/40" />
          <div className="absolute inset-0"><div className="radar-sweep" /></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <div className="w-4 h-4 rounded-full bg-[#00E5FF] glow-primary" />
            <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[9px] font-mono tracking-widest text-[#00E5FF] whitespace-nowrap">YOU</div>
          </div>
          {orbitDots.map((p, i) => (
            <div key={i}
              className={`absolute -translate-x-1/2 -translate-y-1/2 transition-opacity duration-500 ${statsTick % 2 === i % 2 ? "opacity-100" : "opacity-40"}`}
              style={{ top: p.top, left: p.left, animationDelay: `${p.d}s` }}
            >
              <div className="w-3 h-3 rounded-full bg-[#39FF14] glow-secondary dot-pulse" />
            </div>
          ))}
          {/* Faux compat card */}
          <div className="absolute -bottom-2 -right-2 md:-right-8 w-44 p-3 rounded-xl glass border border-[#00E5FF]/30 shadow-[0_0_40px_rgba(0,229,255,0.15)]">
            <div className="text-[9px] tracking-[0.3em] font-mono text-[#00E5FF]">// COMPAT</div>
            <div className="flex items-baseline gap-1 mt-1">
              <span className="font-[Unbounded] font-bold text-3xl text-[#00E5FF]">86</span>
              <span className="text-[10px] font-mono text-white/40">/100</span>
            </div>
            <div className="text-[10px] text-white/70 mt-1">shared: climbing, lofi</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Features() {
  return (
    <section id="features" className="relative py-24 md:py-32 max-w-7xl mx-auto px-4 md:px-8">
      <div className="max-w-3xl">
        <div className="text-[10px] font-mono tracking-[0.3em] text-[#00E5FF]">// CAPABILITIES</div>
        <h2 className="mt-4 font-[Unbounded] font-black tracking-tighter text-4xl md:text-5xl leading-tight">
          A social protocol that respects your <span className="text-[#FF007F]">attention</span>.
        </h2>
        <p className="mt-4 text-white/60 max-w-xl">Five primitives, one product. Everything is built around consent, presence, and clarity.</p>
      </div>
      {/* Bento grid — tetris layout */}
      <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5 auto-rows-[minmax(200px,auto)]">
        {FEATURES.map((f, i) => {
          const c = accentClasses(f.accent);
          const span = [
            "md:col-span-8", "md:col-span-4",
            "md:col-span-4", "md:col-span-8",
            "md:col-span-12",
          ][i];
          return (
            <div key={f.id} id={f.id}
              data-testid={f.testid}
              className={`group relative overflow-hidden ${span} p-8 md:p-10 rounded-2xl border ${c.border} ${c.bg} hover:-translate-y-0.5 hover:${c.glow} transition-[transform,box-shadow] duration-300`}>
              <div className={`inline-flex items-center justify-center w-10 h-10 rounded-xl border ${c.border} ${c.bg} ${c.text}`}>
                <f.icon size={18} />
              </div>
              <h3 className="mt-5 font-[Unbounded] text-xl md:text-2xl tracking-tight">{f.title}</h3>
              <p className="mt-3 text-sm md:text-base text-white/60 leading-relaxed max-w-lg">{f.body}</p>
              <div className={`absolute -right-16 -top-16 w-56 h-56 rounded-full ${c.bg} blur-2xl opacity-60 pointer-events-none`} />
            </div>
          );
        })}
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { n: "01", t: "Share your signal", d: "Turn GPS on. Your position is only shared with people in your scan range — never public.", icon: MapPin },
    { n: "02", t: "See the map light up", d: "Real humans within 25 km show up as pulses. AI computes chemistry before you say hi.", icon: Zap },
    { n: "03", t: "Send a signal", d: "One tap. They Accept or Decline. Decline auto-blocks — the noise never comes back.", icon: Handshake },
    { n: "04", t: "Meet, maybe hangout", d: "Start a Group Hangout for live location sharing during the meetup. Ends on its own.", icon: Users },
  ];
  return (
    <section id="hangouts" className="relative py-24 max-w-7xl mx-auto px-4 md:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1">
          <div className="text-[10px] font-mono tracking-[0.3em] text-[#00E5FF]">// FLOW</div>
          <h2 className="mt-3 font-[Unbounded] font-black tracking-tighter text-3xl md:text-4xl leading-tight">Four beats.<br />That&apos;s it.</h2>
          <p className="mt-4 text-sm text-white/60">Every interaction is opt-in and reversible. No swipes, no leaderboards.</p>
        </div>
        <ol className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-4">
          {steps.map((s) => (
            <li key={s.n} data-testid={`step-${s.n}`} className="p-6 md:p-8 rounded-2xl bg-[#0A0B12] border border-white/10 relative overflow-hidden">
              <div className="flex items-baseline gap-4">
                <span className="font-[Unbounded] text-5xl font-black text-[#00E5FF]/20 tracking-tighter">{s.n}</span>
                <div>
                  <h4 className="font-[Unbounded] text-lg">{s.t}</h4>
                  <p className="mt-2 text-sm text-white/60 max-w-sm leading-relaxed">{s.d}</p>
                </div>
              </div>
              <s.icon size={90} className="absolute -right-4 -bottom-4 text-white/[0.03]" />
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function Advisor() {
  return (
    <section id="advisor" className="relative py-24 max-w-7xl mx-auto px-4 md:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="text-[10px] font-mono tracking-[0.3em] text-[#FF007F]">// AI ADVISOR + SIGNATURE</div>
          <h2 className="mt-4 font-[Unbounded] font-black tracking-tighter text-3xl md:text-4xl leading-tight">
            The AI proposes.<br />
            <span className="text-[#FF007F]">You approve.</span>
          </h2>
          <p className="mt-4 text-white/60 max-w-lg leading-relaxed">
            Set goals. Get concise coaching. Every suggested action requires your private signature to execute — the model never fires anything on its own.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-white/70">
            {[
              "Claude Sonnet 4.6 with your goals as context",
              "Signature is bcrypt-hashed — never leaves the server",
              "Every approve/reject is audited",
            ].map((x) => (
              <li key={x} className="flex items-start gap-2">
                <Check size={14} className="text-[#39FF14] mt-1 shrink-0" />
                <span>{x}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="relative">
          {/* Mock advisor chat card */}
          <div className="glass rounded-2xl border border-white/10 overflow-hidden">
            <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
              <Sparkles size={14} className="text-[#00E5FF]" />
              <span className="font-[Unbounded] text-sm">AI ADVISOR</span>
              <span className="ml-auto text-[10px] font-mono uppercase text-white/40">propose-only</span>
            </div>
            <div className="p-4 space-y-3">
              <div className="inline-block bg-[#00E5FF] text-black text-sm px-3 py-2 rounded-2xl rounded-br-sm">One small step for this week?</div>
              <div className="bg-white/5 border border-white/10 text-sm px-3 py-2 rounded-2xl rounded-bl-sm max-w-[85%]">Do a 25-min focused block on your Portuguese verbs today, then journal 2 lines about what stuck.</div>
              <div className="mt-2 p-3 rounded-xl border border-[#00E5FF]/30 bg-[#00E5FF]/5">
                <div className="flex items-start gap-2">
                  <Sparkles size={12} className="text-[#00E5FF] mt-1" />
                  <div className="text-xs">
                    <div className="font-semibold">Schedule the 25-min block</div>
                    <div className="text-white/60 mt-1">Blocks 15:30–15:55 on your calendar.</div>
                  </div>
                </div>
                <div className="mt-3 flex gap-2">
                  <div className="flex-1 bg-[#05050A] border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white/40 font-mono">••••••</div>
                  <div className="px-3 py-1.5 rounded-lg bg-[#00E5FF] text-black text-xs font-semibold inline-flex items-center gap-1">
                    <ShieldCheck size={12} /> Approve
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SocialProof() {
  const items = [
    { text: "Actually met a co-founder within 3 days of installing.", who: "@nova.7" },
    { text: "Feels like Instagram without the anxiety.", who: "@kai" },
    { text: "The hangout mode is unreal for group trips.", who: "@maya" },
    { text: "Advisor + signature is what AI apps should feel like.", who: "@river" },
    { text: "First app I don't uninstall after a week.", who: "@zed" },
  ];
  const dup = [...items, ...items];
  return (
    <section className="py-16 border-y border-white/5 bg-[#07070E] overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-8 mb-8 flex items-center gap-4">
        <div className="flex -space-x-3">
          {AVATARS.map((a, i) => (
            <img key={i} src={a} alt="" className="w-9 h-9 rounded-full border-2 border-[#05050A] object-cover" />
          ))}
        </div>
        <div>
          <div className="text-[10px] font-mono tracking-[0.3em] text-[#00E5FF]">// EARLY SIGNAL</div>
          <div className="text-sm text-white/60">What early users say</div>
        </div>
      </div>
      <div className="relative overflow-hidden">
        <div className="marquee-track flex gap-4 w-max">
          {dup.map((q, i) => (
            <div key={i} className="w-[340px] shrink-0 p-5 rounded-2xl bg-[#0A0B12] border border-white/10">
              <p className="text-sm text-white/85 leading-relaxed">&ldquo;{q.text}&rdquo;</p>
              <div className="mt-3 text-[10px] font-mono tracking-widest text-white/40">{q.who}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  return (
    <section id="pricing" className="relative py-24 md:py-32 max-w-7xl mx-auto px-4 md:px-8">
      <div className="text-center max-w-2xl mx-auto">
        <div className="text-[10px] font-mono tracking-[0.3em] text-[#00E5FF]">// PRICING</div>
        <h2 className="mt-4 font-[Unbounded] font-black tracking-tighter text-4xl md:text-5xl leading-tight">Free to start. Pro when you want more.</h2>
      </div>
      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl mx-auto">
        <div className="p-8 md:p-10 rounded-2xl bg-[#0A0B12] border border-white/10" data-testid="pricing-free">
          <div className="flex items-center justify-between">
            <div className="text-[10px] font-mono tracking-[0.3em] text-white/50">FREE</div>
            <div className="text-xs font-mono text-white/40">DEFAULT</div>
          </div>
          <div className="mt-4 flex items-baseline gap-1">
            <span className="font-[Unbounded] font-black text-5xl tracking-tighter">$0</span>
            <span className="text-white/40 text-sm">forever</span>
          </div>
          <ul className="mt-6 space-y-2 text-sm text-white/70">
            {["Standard radar (25 km)", "AI compatibility scoring", "Signal handshakes + DMs", "Groups + Hangouts", "Goals + AI Advisor"].map((x) => (
              <li key={x} className="flex items-start gap-2"><Check size={14} className="text-[#39FF14] mt-1 shrink-0" />{x}</li>
            ))}
          </ul>
          <Link to="/auth" data-testid="pricing-free-cta" className="mt-8 block w-full text-center border border-white/15 hover:border-white/40 text-white text-sm font-semibold py-3 rounded-full transition-colors">Create free account</Link>
        </div>

        <div className="relative p-8 md:p-10 rounded-2xl trail-border overflow-hidden" data-testid="pricing-pro">
          <div className="flex items-center justify-between">
            <div className="text-[10px] font-mono tracking-[0.3em] text-[#00E5FF]">PRO</div>
            <div className="text-[10px] font-mono tracking-widest px-2 py-0.5 rounded-full bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/30">DEEPER AI</div>
          </div>
          <div className="mt-4 flex items-baseline gap-1">
            <span className="font-[Unbounded] font-black text-5xl tracking-tighter text-[#00E5FF]">$9</span>
            <span className="text-white/40 text-sm">/month</span>
          </div>
          <ul className="mt-6 space-y-2 text-sm text-white/85">
            {[
              "Everything in Free",
              "Deeper AI reasoning per match",
              "Wider scan range",
              "Priority ranking on other radars",
              "Priority Advisor response",
            ].map((x) => (
              <li key={x} className="flex items-start gap-2"><Check size={14} className="text-[#00E5FF] mt-1 shrink-0" />{x}</li>
            ))}
          </ul>
          <Link to="/auth" data-testid="pricing-pro-cta" className="mt-8 block w-full text-center bg-[#00E5FF] text-black text-sm font-semibold py-3 rounded-full hover:glow-primary transition-shadow">Get Pro</Link>
        </div>
      </div>
    </section>
  );
}

function BigCta() {
  return (
    <section className="relative py-32 overflow-hidden">
      <div className="absolute inset-0 opacity-30"><RadarMap lat={40.7128} lng={-74.006} /></div>
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, transparent 20%, #05050A 80%)" }} />
      <div className="relative max-w-4xl mx-auto px-4 md:px-8 text-center">
        <div className="text-[10px] font-mono tracking-[0.3em] text-[#00E5FF]">// jair.one</div>
        <h2 className="mt-4 font-[Unbounded] font-black tracking-tighter text-5xl md:text-7xl leading-[0.95]">
          Turn the radar on.<br />See who&apos;s <span className="text-[#00E5FF]">there</span>.
        </h2>
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <Link to="/auth" data-testid="bigcta-signup"
            className="inline-flex items-center gap-2 bg-[#00E5FF] text-black font-semibold px-8 py-4 rounded-full text-base hover:glow-primary transition-shadow">
            Start Scanning <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-white/5 py-12 px-4 md:px-8">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between gap-6">
        <div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#00E5FF]" />
            <span className="font-[Unbounded] font-bold tracking-tighter">SIGNAL·RADAR</span>
          </div>
          <div className="mt-2 text-xs font-mono text-white/40">jair.one · proximity social discovery</div>
        </div>
        <div className="text-xs font-mono text-white/40">
          © 2026 · Made with signal, not noise.
        </div>
      </div>
    </footer>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#05050A] text-white antialiased">
      <Nav />
      <Hero />
      <Features />
      <HowItWorks />
      <Advisor />
      <SocialProof />
      <Pricing />
      <BigCta />
      <Footer />
    </div>
  );
}
